import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, QrCode, Link2, Archive, Loader2, ExternalLink } from "lucide-react";
import { adminApi, errMsg } from "@/lib/api";
import { fmtMoney, fmtDate, METHOD_TYPE_LABEL } from "@/lib/format";
import { PageHeader, QrImage, FileUploader, EmptyState, CopyButton } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const EMPTY = { name: "", method_type: "upi_qr", currency: "INR", qr_file_id: null, qr_image_url: null, payment_url: "", account_details: "", instructions: "", min_amount: "100", max_amount: "100000", is_active: true };

function MethodDialog({ open, onClose, initial }) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [qrPreview, setQrPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  React.useEffect(() => {
    if (open) {
      setF(initial ? { ...EMPTY, ...initial, payment_url: initial.payment_url || "", account_details: initial.account_details || "", instructions: initial.instructions || "", min_amount: String(initial.min_amount), max_amount: String(initial.max_amount) } : EMPTY);
      setQrPreview(initial?.qr_image_url || null);
      setErr("");
    }
  }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const uploadQr = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await adminApi.post("/admin/uploads/qr", fd);
      setF((p) => ({ ...p, qr_file_id: r.data.file_id, qr_upload: r.data }));
      setQrPreview(r.data.url);
      toast.success("QR image uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
      throw e;
    }
  };

  const save = async () => {
    setErr("");
    if (f.name.trim().length < 2) return setErr("Enter a method name");
    if (f.method_type === "upi_qr" && !f.qr_file_id) return setErr("Upload a QR code image");
    if (f.method_type === "payment_link" && !f.payment_url.trim()) return setErr("Enter the payment link");
    const min = parseFloat(f.min_amount), max = parseFloat(f.max_amount);
    if (!(min > 0) || !(max > 0)) return setErr("Enter valid minimum and maximum amounts");
    if (max < min) return setErr("Maximum must be greater than or equal to minimum");
    const body = { name: f.name.trim(), method_type: f.method_type, currency: f.currency, qr_file_id: f.qr_file_id, payment_url: f.payment_url.trim() || null,
      account_details: f.account_details.trim() || null, instructions: f.instructions, min_amount: min, max_amount: max, is_active: f.is_active };
    setBusy(true);
    try {
      if (initial) await adminApi.put(`/admin/payment-methods/${initial.id}`, body);
      else await adminApi.post("/admin/payment-methods", body);
      toast.success(initial ? "Payment method updated" : "Payment method added", { description: "Changes are live on the user deposit page." });
      qc.invalidateQueries({ queryKey: ["admin-methods"] });
      onClose();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="payment-method-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit payment method" : "Add payment method"}</DialogTitle>
          <DialogDescription>Users see active methods on the deposit page immediately after saving.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label>Method name</Label><Input value={f.name} onChange={set("name")} placeholder="e.g. PhonePe Business UPI" data-testid="method-name-input" /></div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={f.method_type} onValueChange={set("method_type")}>
              <SelectTrigger data-testid="method-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="upi_qr" data-testid="method-type-option-qr">UPI QR code</SelectItem><SelectItem value="payment_link" data-testid="method-type-option-link">Payment link</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <Select value={f.currency} onValueChange={set("currency")}>
              <SelectTrigger data-testid="method-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="INR" data-testid="method-currency-option-inr">INR</SelectItem><SelectItem value="USDT" data-testid="method-currency-option-usdt">USDT</SelectItem></SelectContent>
            </Select>
          </div>
          {f.method_type === "upi_qr" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>QR code image</Label>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                {qrPreview && <QrImage url={qrPreview} className="h-32 w-32 shrink-0" testId="method-qr-preview" />}
                <div className="flex-1">
                  <FileUploader accept="image/png,image/jpeg,image/webp" label={qrPreview ? "Replace QR image" : "Upload QR image"} hint="PNG, JPG or WEBP · max 5 MB" testId="method-qr-uploader"
                    value={f.qr_upload} onUpload={uploadQr} onClear={() => setF((p) => ({ ...p, qr_upload: null }))} />
                </div>
              </div>
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{f.method_type === "payment_link" ? "Payment link" : "UPI deep link (optional)"}</Label>
            <Input value={f.payment_url} onChange={set("payment_url")} placeholder={f.method_type === "payment_link" ? "https://rzp.io/l/your-page" : "upi://pay?pa=merchant@upi&pn=Name"} data-testid="method-payment-url-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>UPI ID / account / wallet address (optional)</Label><Input value={f.account_details} onChange={set("account_details")} placeholder="merchant@okaxis" data-testid="method-account-details-input" /></div>
          <div className="space-y-1.5"><Label>Minimum deposit ({f.currency})</Label><Input inputMode="decimal" value={f.min_amount} onChange={set("min_amount")} data-testid="method-min-amount-input" /></div>
          <div className="space-y-1.5"><Label>Maximum deposit ({f.currency})</Label><Input inputMode="decimal" value={f.max_amount} onChange={set("max_amount")} data-testid="method-max-amount-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Instructions for users</Label><Textarea rows={4} value={f.instructions} onChange={set("instructions")} placeholder="e.g. Pay the exact amount. Add your registered email in the payment note." data-testid="method-instructions-input" /></div>
          <label className="flex items-center justify-between rounded-lg border p-3 sm:col-span-2">
            <span><span className="block text-sm font-medium">Active</span><span className="text-xs text-muted-foreground">Inactive methods are hidden from users and reject new deposits.</span></span>
            <Switch checked={f.is_active} onCheckedChange={set("is_active")} data-testid="method-active-switch" />
          </label>
        </div>
        {err && <p className="text-sm text-rose-700" data-testid="method-form-error">{err}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="method-cancel-button">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="method-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{initial ? "Save changes" : "Add method"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPaymentMethods() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [archiving, setArchiving] = useState(null);
  const [filter, setFilter] = useState("all");
  const { data, isLoading } = useQuery({ queryKey: ["admin-methods"], queryFn: () => adminApi.get("/admin/payment-methods").then((r) => r.data) });

  const toggle = async (m, v) => {
    try {
      await adminApi.patch(`/admin/payment-methods/${m.id}/status`, { is_active: v });
      toast.success(`${m.name} ${v ? "activated" : "deactivated"}`);
      qc.invalidateQueries({ queryKey: ["admin-methods"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const archive = async () => {
    try {
      await adminApi.delete(`/admin/payment-methods/${archiving.id}`);
      toast.success("Method archived");
      qc.invalidateQueries({ queryKey: ["admin-methods"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setArchiving(null);
    }
  };
  const items = (data || []).filter((m) => filter === "all" || (filter === "active" ? m.is_active : filter === "inactive" ? !m.is_active : m.currency === filter));

  return (
    <div>
      <PageHeader title="Payment methods" subtitle="Manage UPI QR codes and payment links shown on the user deposit page."
        actions={<Button className="gap-2" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-payment-method-button"><Plus className="h-4 w-4" /> Add method</Button>} />
      <div className="mb-4 flex flex-wrap gap-2">
        {[["all", "All"], ["active", "Active"], ["inactive", "Inactive"], ["INR", "INR"], ["USDT", "USDT"]].map(([k, l]) => (
          <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`methods-filter-${k.toLowerCase()}`}>{l}</Button>
        ))}
      </div>
      {isLoading ? <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-56" />)}</div> : items.length === 0 ? (
        <EmptyState icon={QrCode} title="No payment methods" testId="methods-empty">Add a UPI QR code or payment link so users can deposit.</EmptyState>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((m) => (
            <Card key={m.id} className={cn("card-hover", !m.is_active && "bg-zinc-50")} data-testid="admin-method-card" data-method-id={m.id}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  {m.method_type === "upi_qr" ? <QrImage url={m.qr_image_url} className="h-20 w-20 shrink-0" /> : (
                    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border bg-secondary"><Link2 className="h-7 w-7 text-primary" /></div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold" data-testid="admin-method-name">{m.name}</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <span className="rounded-md bg-secondary px-1.5 py-0.5 text-[11px] font-medium">{METHOD_TYPE_LABEL[m.method_type]}</span>
                      <span className="rounded-md bg-secondary px-1.5 py-0.5 text-[11px] font-medium">{m.currency}</span>
                      <span className={cn("rounded-md px-1.5 py-0.5 text-[11px] font-medium", m.is_active ? "bg-emerald-50 text-emerald-800" : "bg-zinc-200 text-zinc-700")} data-testid="admin-method-status">{m.is_active ? "Active" : "Inactive"}</span>
                    </div>
                    <p className="num mt-2 text-xs text-muted-foreground">{fmtMoney(m.min_amount, m.currency)} – {fmtMoney(m.max_amount, m.currency)}</p>
                  </div>
                  <Switch checked={m.is_active} onCheckedChange={(v) => toggle(m, v)} data-testid="admin-method-active-toggle" aria-label="Toggle active" />
                </div>
                {m.payment_url && (
                  <div className="mt-3 flex items-center gap-2 rounded-lg border p-2">
                    <p className="num min-w-0 flex-1 truncate text-xs">{m.payment_url}</p>
                    <CopyButton value={m.payment_url} label="" variant="ghost" className="h-7 w-7 p-0" testId="admin-method-copy-link" />
                    <a href={m.payment_url} target="_blank" rel="noopener noreferrer" aria-label="Open link" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-4 w-4" /></a>
                  </div>
                )}
                {m.instructions && <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">{m.instructions}</p>}
                <div className="mt-4 flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
                  <span>{m.request_count} requests · {m.pending_count} open</span>
                  <span>Updated {fmtDate(m.updated_at, false)}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => { setEditing(m); setOpen(true); }} data-testid="admin-method-edit-button"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                  <Button size="sm" variant="ghost" className="gap-1.5 text-rose-700" onClick={() => setArchiving(m)} data-testid="admin-method-archive-button"><Archive className="h-3.5 w-3.5" /> Archive</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <MethodDialog open={open} initial={editing} onClose={() => setOpen(false)} />
      <AlertDialog open={!!archiving} onOpenChange={(o) => !o && setArchiving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Archive {archiving?.name}?</AlertDialogTitle><AlertDialogDescription>It will be removed from the deposit page. Existing deposit requests and history are kept.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="archive-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={archive} data-testid="archive-confirm">Archive</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
